import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, ListGroup, Badge, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import { 
  FaSignature, FaCalendarAlt, FaUser, FaFont, 
  FaCheckSquare, FaSave, FaTrash, FaGripVertical,
  FaUndo, FaInfoCircle, FaMagic, FaRobot, FaEye,
  FaCheckCircle, FaExclamationTriangle
} from 'react-icons/fa';
import PDFImageViewer from './PDFImageViewer';
import FieldMappingDebugger from './FieldMappingDebugger';
import documentFieldsService, { DetectedField, BrokerageTemplate } from '../../services/documentFieldsService';
import axiosInstance from '../../helpers/api';
import { 
  CLIENT_FIELD_PLACEHOLDERS, 
  getHighPriorityFields,
  getFieldsByParty
} from '../../constants/clientFieldPlaceholders';
import './DocumentFieldMapper.css';

interface Field {
  id: string;
  type: 'signature' | 'date' | 'text' | 'initial' | 'checkbox';
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  page: number;
  mapping?: string;
  required?: boolean;
}

interface ImageData {
  page: number;
  url: string;
  width: number;
  height: number;
}

interface DocumentFieldMapperProps {
  documentUrl: string;
  documentName: string;
  documentId: string;
  clientData?: any;
  onSaveMapping?: (fields: Field[]) => void;
  onFieldsUpdate?: (fields: Field[]) => void;
  existingFields?: Field[];
  initialFields?: Field[];
  detectedFields?: any[]; // Legacy prop - now using API
  brokerageId?: string;
  mode?: 'full' | 'signature-only';
  signers?: Array<{ name: string; email: string; role: string; }>;
}

const DocumentFieldMapper: React.FC<DocumentFieldMapperProps> = ({
  documentUrl,
  documentName,
  documentId,
  clientData,
  onSaveMapping,
  onFieldsUpdate,
  existingFields = [],
  initialFields = [],
  detectedFields = [], // Legacy prop
  brokerageId,
  mode = 'full',
  signers = []
}) => {
  // Field state
  const [fields, setFields] = useState<Field[]>(initialFields.length > 0 ? initialFields : existingFields);
  const [selectedField, setSelectedField] = useState<Field | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedFieldType, setDraggedFieldType] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfImages, setPdfImages] = useState<ImageData[]>([]);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | undefined>();

  // Field detection state
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState<string | null>(null);
  const [aiDetectedFields, setAiDetectedFields] = useState<DetectedField[]>([]);
  const [fieldDetectionStatus, setFieldDetectionStatus] = useState<{
    hasFields: boolean;
    confidence?: number;
    detectionMethod?: string;
    fieldBreakdown?: any;
  }>({ hasFields: false });
  const [templates, setTemplates] = useState<BrokerageTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Load field mappings on component mount
  useEffect(() => {
    loadFieldMappings();
    loadTemplateIfAvailable();
  }, [documentId]);

  // Notify parent component when fields change
  useEffect(() => {
    if (onFieldsUpdate) {
      onFieldsUpdate(fields);
    }
  }, [fields, onFieldsUpdate]);

  // Load available templates for this brokerage so agents can reuse mappings
  useEffect(() => {
    if (!brokerageId) return;

    let isCancelled = false;

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      setTemplateError(null);
      try {
        const response = await documentFieldsService.getBrokerageTemplates(brokerageId);
        if (!isCancelled && response.success) {
          setTemplates(response.templates || []);
        }
      } catch (error: any) {
        console.error('Error loading templates:', error);
        if (!isCancelled) {
          setTemplateError('Failed to load templates.');
        }
      } finally {
        if (!isCancelled) {
          setLoadingTemplates(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      isCancelled = true;
    };
  }, [brokerageId]);

  // Auto-add signature fields in signature-only mode if none exist
  useEffect(() => {
    if (mode === 'signature-only' && fields.length === 0 && signers.length > 0) {
      console.log('🖊️ Auto-adding signature fields for signers');
      const defaultFields = signers.map((signer, index) => ({
        id: `sig-${Date.now()}-${index}`,
        type: 'signature' as const,
        label: `${signer.name} Signature`,
        x: 20,
        y: 70 + (index * 10),
        width: 25,
        height: 5,
        page: 1,
        mapping: `${signer.role}${index + 1}_signature`,
        required: true
      }));
      setFields(defaultFields);
    }
  }, [mode, signers]);

  // Load template-based field mappings if available
  const loadTemplateIfAvailable = async () => {
    try {
      const docType = getDocumentTypeFromName(documentName);
      if (!docType) return;

      console.log(`🎯 Checking for template for document type: ${docType}`);
      
      const response = await axiosInstance.get(`/templates/field-mapping/${docType}`);
      
      if (response.data.success && response.data.fields.length > 0) {
        console.log(`✅ Loaded ${response.data.fields.length} fields from template for ${docType}`);
        
        const templateFields: Field[] = response.data.fields.map((field: any) => ({
          id: field.id || `template_${Date.now()}_${Math.random()}`,
          type: field.type,
          label: field.label,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          page: field.page || 1,
          mapping: field.mapping,
          required: field.required
        }));
        
        setFields(templateFields);
        setFieldDetectionStatus({
          hasFields: true,
          confidence: response.data.confidence_score,
          detectionMethod: 'template',
          fieldBreakdown: {
            signature: templateFields.filter(f => f.type === 'signature').length,
            text: templateFields.filter(f => f.type === 'text').length,
            date: templateFields.filter(f => f.type === 'date').length,
            checkbox: templateFields.filter(f => f.type === 'checkbox').length,
            initial: templateFields.filter(f => f.type === 'initial').length
          }
        });
        return true;
      }
    } catch (error) {
      console.log('No template found for this document type');
    }
    return false;
  };

  const applyTemplateFields = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplateId('');
      return;
    }

    setApplyingTemplate(true);
    setTemplateError(null);
    try {
      const response = await documentFieldsService.getFieldMappings(templateId);
      if (response.success && response.fields?.length) {
        const templateFields: Field[] = response.fields.map((field) => ({
          id: field.id || `template_${Date.now()}_${Math.random()}`,
          type: field.type,
          label: field.label,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          page: field.page || 1,
          mapping: field.mapping,
          required: field.required !== false
        }));

        setFields(templateFields);
        setSelectedField(null);
        setFieldDetectionStatus({
          hasFields: true,
          detectionMethod: response.detectionMethod || 'template',
          confidence: response.confidence,
          fieldBreakdown: response.fieldBreakdown
        });

        if (onFieldsUpdate) {
          onFieldsUpdate(templateFields);
        }

        if (documentId) {
          try {
            const payloadFields: DetectedField[] = templateFields.map((field) => ({
              id: field.id,
              type: field.type,
              label: field.label,
              page: field.page,
              x: field.x,
              y: field.y,
              width: field.width,
              height: field.height,
              confidence: 1,
              source: 'manual',
              mapping: field.mapping,
              required: field.required
            }));
            await documentFieldsService.updateFieldMappings(documentId, payloadFields);
          } catch (persistError) {
            console.warn('Template fields applied but failed to persist mapping:', persistError);
          }
        }
      } else {
        setTemplateError('Selected template has no saved fields.');
      }
    } catch (error: any) {
      console.error('Failed to apply template fields:', error);
      setTemplateError(error?.message || 'Failed to apply template fields.');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const saveFieldsAsTemplate = async () => {
    if (fields.length === 0) {
      setDetectionError('No fields to save as template');
      return;
    }

    const docType = getDocumentTypeFromName(documentName);
    if (!docType) {
      setDetectionError('Cannot determine document type for template saving');
      return;
    }

    try {
      const response = await axiosInstance.post('/templates/save-field-mapping', {
        document_type: docType,
        document_name: documentName,
        field_mappings: fields,
        document_id: documentId
      });

      if (response.data.success) {
        console.log(`✅ Saved ${fields.length} fields as template for ${docType}`);
        alert(`Template saved! Future ${docType} documents will automatically load these ${fields.length} fields.`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setDetectionError('Failed to save template');
    }
  };

  // Track container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (pdfContainerRef.current) {
        const rect = pdfContainerRef.current.getBoundingClientRect();
        setContainerDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const loadFieldMappings = async () => {
    if (!documentId) return;
    
    setIsLoadingFields(true);
    try {
      const response = await documentFieldsService.getFieldMappings(documentId);
      
      if (response.hasFieldMappings) {
        console.log(`✅ Loaded ${response.fields.length} AI-detected fields`);
        
        // Convert API fields to component fields
        const convertedFields: Field[] = response.fields.map(field => ({
          id: field.id,
          type: field.type,
          label: field.label,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          page: field.page,
          mapping: field.mapping,
          required: field.required
        }));
        
        setFields(convertedFields);
        setAiDetectedFields(response.fields);
        setFieldDetectionStatus({
          hasFields: true,
          confidence: response.confidence,
          detectionMethod: response.detectionMethod,
          fieldBreakdown: response.fieldBreakdown
        });
      } else {
        console.log('ℹ️ No existing field mappings found');
        setFieldDetectionStatus({ hasFields: false });
      }
    } catch (error) {
      console.error('Error loading field mappings:', error);
      setDetectionError('Failed to load existing field mappings');
    } finally {
      setIsLoadingFields(false);
    }
  };

  const triggerFieldDetection = async (forceRedetect = false) => {
    if (!documentId) return;
    
    setIsDetecting(true);
    setDetectionError(null);
    
    try {
      console.log(`🤖 Starting field detection for document: ${documentId}`);
      
      const response = await documentFieldsService.detectFields(documentId, forceRedetect);
      
      if (response.success) {
        if (response.alreadyDetected && !forceRedetect) {
          console.log('ℹ️ Fields already detected, loading existing mappings');
          await loadFieldMappings();
        } else {
          console.log(`✅ Field detection completed: ${response.fieldCount} fields found`);
          
          // Convert detected fields to component fields
          const convertedFields: Field[] = (response.fields || []).map(field => ({
            id: field.id,
            type: field.type,
            label: field.label,
            x: field.x,
            y: field.y,
            width: field.width,
            height: field.height,
            page: field.page,
            mapping: field.mapping,
            required: field.required
          }));
          
          setFields(convertedFields);
          setAiDetectedFields(response.fields || []);
          setFieldDetectionStatus({
            hasFields: true,
            confidence: response.confidence,
            detectionMethod: 'hybrid',
            fieldBreakdown: response.fieldBreakdown
          });
        }
      }
    } catch (error: any) {
      console.error('Field detection failed:', error);
      setDetectionError(error.message || 'Failed to detect fields');
    } finally {
      setIsDetecting(false);
    }
  };

  // Determine document type from filename
  const getDocumentTypeFromName = (fileName: string): string | null => {
    const name = fileName.toLowerCase();
    
    if (name.includes('residential') && name.includes('purchase')) {
      return 'residential_purchase_contract';
    }
    if (name.includes('listing') && (name.includes('contract') || name.includes('agreement'))) {
      return 'listing_contract_addendum';
    }
    if (name.includes('buyer') && name.includes('broker')) {
      return 'buyer_broker_agreement';
    }
    if (name.includes('purchase') && name.includes('contract')) {
      return 'residential_purchase_contract';
    }
    if (name.includes('addendum')) {
      return 'listing_contract_addendum';
    }
    
    return null;
  };

  // Debug logging
  console.log('DocumentFieldMapper - AI detected fields:', aiDetectedFields.length);
  console.log('DocumentFieldMapper - Current fields:', fields.length);

  // Field templates for dragging
  const fieldTemplates = [
    { type: 'signature', label: 'Signature', icon: FaSignature, color: '#a855f7' },
    { type: 'date', label: 'Date', icon: FaCalendarAlt, color: '#22c55e' },
    { type: 'text', label: 'Text/Name', icon: FaUser, color: '#3b82f6' },
    { type: 'initial', label: 'Initials', icon: FaFont, color: '#fb923c' },
    { type: 'checkbox', label: 'Checkbox', icon: FaCheckSquare, color: '#ef4444' }
  ];

  // Handle drag start from the field palette
  const handleDragStart = (fieldType: string) => {
    setIsDragging(true);
    setDraggedFieldType(fieldType);
  };

  // Handle drop on the PDF
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedFieldType || !pdfContainerRef.current) return;

    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    let newField: Field;

    // Check if it's a detected field being dragged
    if (draggedFieldType.startsWith('detected-')) {
      const detectedIndex = parseInt(draggedFieldType.replace('detected-', ''));
      const detectedField = detectedFields[detectedIndex];
      
      if (!detectedField) return;

      const fieldType = detectedField.type || 'text';
      newField = {
        id: `field_${Date.now()}`,
        type: fieldType as Field['type'],
        label: detectedField.name || detectedField.label || 'Field',
        x: Math.max(0, Math.min(85, x)),
        y: Math.max(0, Math.min(90, y)),
        width: fieldType === 'signature' ? 25 : 20,
        height: fieldType === 'signature' ? 8 : 3,
        page: currentPage,
        mapping: detectedField.mapping || detectedField.name,
        required: detectedField.required !== false
      };
    } else {
      // It's a new field template being dragged
      const template = fieldTemplates.find(t => t.type === draggedFieldType);
      if (!template) return;

      newField = {
        id: `field_${Date.now()}`,
        type: draggedFieldType as Field['type'],
        label: template.label,
        x: Math.max(0, Math.min(85, x)),
        y: Math.max(0, Math.min(90, y)),
        width: draggedFieldType === 'signature' ? 25 : 20,
        height: draggedFieldType === 'signature' ? 8 : 3,
        page: currentPage,
        required: true
      };
    }

    setFields([...fields, newField]);
    setSelectedField(newField);
    setIsDragging(false);
    setDraggedFieldType(null);
  };

  // Handle field selection
  const handleFieldClick = (field: Field) => {
    setSelectedField(field);
  };

  // Handle field deletion
  const handleDeleteField = (fieldId: string) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (selectedField?.id === fieldId) {
      setSelectedField(null);
    }
  };

  // Handle field property changes
  const handleFieldUpdate = (fieldId: string, updates: Partial<Field>) => {
    setFields(fields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  };

  // Handle field resize
  const handleResize = (field: Field, direction: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = field.width;
    const startHeight = field.height;

    const handleMouseMove = (e: MouseEvent) => {
      if (!pdfContainerRef.current) return;
      const rect = pdfContainerRef.current.getBoundingClientRect();
      const deltaX = ((e.clientX - startX) / rect.width) * 100;
      const deltaY = ((e.clientY - startY) / rect.height) * 100;

      let updates: Partial<Field> = {};

      if (direction.includes('right')) {
        updates.width = Math.max(10, startWidth + deltaX);
      }
      if (direction.includes('bottom')) {
        updates.height = Math.max(3, startHeight + deltaY);
      }

      handleFieldUpdate(field.id, updates);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Save field mappings
  const handleSave = async () => {
    // Save mappings
    if (onSaveMapping) {
      onSaveMapping(fields);
    }
    
    // Submit training data if there are mapped fields
    const mappedFields = fields.filter(f => f.mapping);
    if (mappedFields.length > 0) {
      try {
        const response = await axiosInstance.post('/document-field-training', {
          documentId,
          documentName,
          documentType: 'listing_contract_addendum', // TODO: Detect from document
          fields: fields.map(f => ({
            id: f.id,
            type: f.type,
            label: f.label,
            mapping: f.mapping || null,
            x: f.x,
            y: f.y,
            width: f.width,
            height: f.height,
            page: f.page,
            required: f.required,
            confidence: (f as any).confidence || 0.9
          })),
          brokerageId
        });
        
        console.log('Training data saved:', response.data);
      } catch (error) {
        console.error('Failed to save training data:', error);
      }
    }
    
    setShowSaveConfirm(true);
    setTimeout(() => setShowSaveConfirm(false), 3000);
  };

  // Get field value based on mapping
  const getFieldValue = (field: Field) => {
    if (field.mapping) {
      const placeholder = CLIENT_FIELD_PLACEHOLDERS.find(p => p.id === field.mapping);
      if (placeholder) {
        // Return a sample value based on field type
        switch (placeholder.fieldType) {
          case 'signature':
            return `[${placeholder.label}]`;
          case 'date':
            return 'MM/DD/YYYY';
          case 'initial':
            return '[Init]';
          default:
            return placeholder.label;
        }
      }
    }
    
    // Default values for unmapped fields
    switch (field.type) {
      case 'signature':
        return '[Signature Needed]';
      case 'date':
        return 'Date Needed';
      case 'initial':
        return '[Initial]';
      case 'checkbox':
        return '☐';
      default:
        return field.label || 'Field';
    }
  };

  return (
    <div className="document-field-mapper">
      {/* Loading State */}
      {isLoadingFields && (
        <Alert variant="info" className="m-3">
          <Spinner size="sm" className="me-2" />
          Loading existing field mappings...
        </Alert>
      )}

      {brokerageId && (
        <div className="m-3">
          <div className="d-flex align-items-center justify-content-between">
            <label className="form-label small fw-semibold mb-1">
              Use Existing Field Mapping
            </label>
            {loadingTemplates && <Spinner animation="border" size="sm" />}
          </div>
          <Form.Select
            size="sm"
            value={selectedTemplateId}
            disabled={loadingTemplates || applyingTemplate || templates.length === 0}
            onChange={(e) => {
              const templateId = e.target.value;
              setSelectedTemplateId(templateId);
              if (templateId) {
                applyTemplateFields(templateId);
              } else {
                setTemplateError(null);
              }
            }}
          >
            <option value="">-- Select Template --</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.fieldCount ?? 0} fields{tpl.hasFillableTemplate ? ' • Fillable' : ''})
              </option>
            ))}
          </Form.Select>
          {templateError && (
            <div className="text-danger small mt-1">{templateError}</div>
          )}
          {applyingTemplate && (
            <div className="text-muted small mt-1">Applying template…</div>
          )}
          {selectedTemplateId && templates.find(t => t.id === selectedTemplateId)?.hasFillableTemplate && (
            <div className="mt-2 d-flex align-items-center">
              <Badge bg="success" className="me-2">PDFFiller</Badge>
              <small className="text-muted">Editable fillable template available.</small>
              {templates.find(t => t.id === selectedTemplateId)?.pdffillerEditUrl && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    const tpl = templates.find(t => t.id === selectedTemplateId);
                    if (tpl?.pdffillerEditUrl) {
                      window.open(tpl.pdffillerEditUrl, '_blank', 'noopener');
                    }
                  }}
                >
                  Open in PDFFiller
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Detection Error */}
      {detectionError && (
        <Alert variant="danger" className="m-3" dismissible onClose={() => setDetectionError(null)}>
          <FaExclamationTriangle className="me-2" />
          <strong>Detection Error:</strong> {detectionError}
        </Alert>
      )}

      {/* AI Field Detection Status */}
      {!isLoadingFields && (
        <div className="field-detection-status m-3">
          {fieldDetectionStatus.hasFields ? (
            <Alert variant="success" className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <FaRobot className="me-2" />
                  <strong>AI Detected {fields.length} Fields</strong>
                  {fieldDetectionStatus.confidence && (
                    <Badge bg="info" className="ms-2">
                      {documentFieldsService.formatConfidence(fieldDetectionStatus.confidence)} Confidence
                    </Badge>
                  )}
                </div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => triggerFieldDetection(true)}
                  disabled={isDetecting}
                >
                  {isDetecting ? <Spinner size="sm" /> : <FaMagic />}
                  {isDetecting ? ' Re-detecting...' : ' Re-detect'}
                </Button>
              </div>
            </Alert>
          ) : (
            <Alert variant="warning" className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <FaInfoCircle className="me-2" />
                  <strong>No Fields Detected</strong>
                  <span className="ms-2 small text-muted">
                    AI detection failed or found no client signature areas
                  </span>
                </div>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    className="me-2"
                    onClick={() => triggerFieldDetection(true)}
                    disabled={isDetecting}
                  >
                    {isDetecting ? <Spinner size="sm" /> : <FaMagic />}
                    {isDetecting ? ' Retrying...' : ' Retry AI'}
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => {
                      // Add a signature field template in center of document
                      const newField = {
                        id: `manual_${Date.now()}`,
                        type: 'signature' as Field['type'],
                        label: 'Manual Field',
                        x: 25,
                        y: 80,
                        width: 30,
                        height: 8,
                        page: currentPage,
                        required: true
                      };
                      setFields([...fields, newField]);
                      setSelectedField(newField);
                    }}
                  >
                    + Manual Field
                  </Button>
                </div>
              </div>
            </Alert>
          )}
        </div>
      )}

      {/* Instructions */}
      {fields.length === 0 && !fieldDetectionStatus.hasFields && !isDetecting && (
        <Alert variant="secondary" className="m-3">
          <FaInfoCircle className="me-2" />
          <strong>Getting Started:</strong> Use AI field detection above, or manually drag field types from the left panel onto the document. 
          Once placed, you can move and resize them. Click "Save Field Mapping" when done to reuse this layout for similar documents.
        </Alert>
      )}
      
      {/* Compact Mapping Guide */}
      {fields.length > 0 && fields.filter(f => !f.mapping).length > 0 && (
        <Alert variant="info" className="m-3 py-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>🎯 Focus on client signature areas:</strong> 
              <span className="ms-2 small text-muted">
                Signatures, printed names, dates, initials only
              </span>
            </div>
            <Badge bg="warning">{fields.filter(f => !f.mapping).length} need mapping</Badge>
          </div>
        </Alert>
      )}
      
      <div className="mapper-container">
        {/* Field Palette - Left Side */}
        <Card className="field-palette">
          <Card.Header>
            <h6 className="mb-0">
              <FaGripVertical className="me-2" />
              Field Management
            </h6>
          </Card.Header>
          <Card.Body>
            {/* AI-Detected Fields Section */}
            {aiDetectedFields.length > 0 && (
              <>
                <h6 className="mb-3 text-success">
                  <FaRobot className="me-2" />
                  AI-Detected Fields ({aiDetectedFields.length})
                </h6>
                <p className="small text-muted mb-2">
                  These fields were automatically detected. They're already positioned on the document:
                </p>
                <ListGroup variant="flush" className="mb-4">
                  {aiDetectedFields.map((field, index) => {
                    const template = fieldTemplates.find(t => t.type === field.type) || fieldTemplates[2];
                    const Icon = template?.icon || FaUser;
                    const color = template?.color || '#3b82f6';
                    
                    return (
                      <ListGroup.Item
                        key={`ai-detected-${field.id}`}
                        className={`field-template ${selectedField?.id === field.id ? 'active' : ''}`}
                        onClick={() => {
                          const componentField = fields.find(f => f.id === field.id);
                          if (componentField) setSelectedField(componentField);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <Icon className="me-2" style={{ color }} />
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{field.label}</div>
                            <small className="text-muted">
                              Page {field.page} • {documentFieldsService.formatConfidence(field.confidence)} confidence
                              {field.source && (
                                <span className="ms-1">
                                  ({field.source === 'textract' ? 'AWS Textract' : 
                                    field.source === 'gpt_vision' ? 'GPT-4 Vision' : 'Manual'})
                                </span>
                              )}
                            </small>
                          </div>
                          <div className="d-flex align-items-center">
                            <Badge 
                              bg={field.confidence > 0.8 ? 'success' : field.confidence > 0.6 ? 'warning' : 'secondary'} 
                              className="ms-2"
                            >
                              <FaEye className="me-1" />
                              AI
                            </Badge>
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
                <hr />
              </>
            )}

            {/* Quick Client Fields */}
            <h6 className="mb-3">
              <FaFont className="me-2" />
              Quick Client Fields
            </h6>
            <div className="row g-2 mb-3">
              {[
                { type: 'signature', label: 'Signature', icon: '✍️' },
                { type: 'text', label: 'Name', icon: '📝' },
                { type: 'date', label: 'Date', icon: '📅' },
                { type: 'initial', label: 'Initial', icon: '✏️' }
              ].map(fieldType => (
                <div key={fieldType.type} className="col-6">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={() => {
                      const newField = {
                        id: `quick_${Date.now()}`,
                        type: fieldType.type as Field['type'],
                        label: fieldType.label,
                        x: 20 + Math.random() * 40, // Random position
                        y: 70 + Math.random() * 20,
                        width: fieldType.type === 'signature' ? 30 : 20,
                        height: fieldType.type === 'signature' ? 8 : 4,
                        page: currentPage,
                        required: true
                      };
                      setFields([...fields, newField]);
                      setSelectedField(newField);
                    }}
                  >
                    {fieldType.icon} {fieldType.label}
                  </Button>
                </div>
              ))}
            </div>
            
            <hr />
            
            {/* Create New Fields Section */}
            <h6 className="mb-3">
              <FaGripVertical className="me-2" />
              Drag Fields
            </h6>
            <p className="small text-muted mb-2">
              Or drag field types onto the document:
            </p>
            <ListGroup variant="flush">
              {fieldTemplates.map(template => (
                <ListGroup.Item
                  key={template.type}
                  className="field-template"
                  draggable
                  onDragStart={() => handleDragStart(template.type)}
                  onDragEnd={() => {
                    setIsDragging(false);
                    setDraggedFieldType(null);
                  }}
                  style={{ cursor: 'grab' }}
                >
                  <div className="d-flex align-items-center">
                    <FaGripVertical className="text-muted me-2" />
                    <template.icon className="me-2" style={{ color: template.color }} />
                    <span>{template.label}</span>
                    <small className="ms-auto text-muted">Drag to add</small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>

            <hr />

            {/* Field List */}
            <h6 className="mb-2">Placed Fields ({fields.length})</h6>
            <ListGroup variant="flush" className="placed-fields-list">
              {fields.map(field => (
                <ListGroup.Item
                  key={field.id}
                  className={`d-flex justify-content-between align-items-center ${
                    selectedField?.id === field.id ? 'active' : ''
                  }`}
                  onClick={() => handleFieldClick(field)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <Badge bg="secondary" className="me-2">Page {field.page}</Badge>
                    <span>{field.label}</span>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteField(field.id);
                    }}
                  >
                    <FaTrash />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>

            {fields.length === 0 && (
              <div className="text-center text-muted py-3">
                <small>Drag fields to the document to start mapping</small>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* PDF Viewer - Center */}
        <div className="pdf-viewer-container">
          <div className="pdf-controls mb-2">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={fields.length === 0}
              className="me-2"
            >
              <FaSave className="me-2" />
              Save Field Mapping
            </Button>
            {fields.length > 0 && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={saveFieldsAsTemplate}
                title="Save these fields as a reusable template for this document type"
              >
                📑 Save Template
              </Button>
            )}
            {fields.length > 0 && (
              <>
                <div className="d-flex align-items-center">
                  <Badge bg="info" className="me-2">
                    {fields.filter(f => f.mapping).length}/{fields.length} mapped
                  </Badge>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="me-2"
                    onClick={() => {
                      // Export training data
                      const trainingData = {
                        documentName,
                        documentType: 'listing_contract_addendum',
                        fields: fields.map(f => ({
                          type: f.type,
                          label: f.label,
                          mapping: f.mapping,
                          x: f.x,
                          y: f.y,
                          width: f.width,
                          height: f.height,
                          page: f.page
                        }))
                      };
                      console.log('Training data:', JSON.stringify(trainingData, null, 2));
                      alert('Training data logged to console. This will help improve future detection!');
                    }}
                  >
                    Export Training Data
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      if (window.confirm('Clear all fields?')) {
                        setFields([]);
                        setSelectedField(null);
                      }
                    }}
                  >
                    <FaUndo className="me-2" />
                    Clear All
                  </Button>
                </div>
              </>
            )}
          </div>

          <div
            ref={pdfContainerRef}
            className="pdf-container"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            style={{ position: 'relative' }}
          >
            {/* PDF Image Viewer for accurate field positioning */}
            <PDFImageViewer
              documentUrl={documentUrl}
              documentId={documentId}
              onPageChange={setCurrentPage}
              onImagesLoaded={setPdfImages}
            >
              {/* Field Overlays - now inside the PDFImageViewer */}
              {fields.filter(f => f.page === currentPage).map(field => {
              const template = fieldTemplates.find(t => t.type === field.type);
              const color = template?.color || '#3b82f6';
              const Icon = template?.icon || FaUser;

              return (
                <div
                  key={field.id}
                  className={`field-overlay ${selectedField?.id === field.id ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${field.x}%`,
                    top: `${field.y}%`,
                    width: `${field.width}%`,
                    height: `${field.height}%`,
                    backgroundColor: `${color}1A`,
                    border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${color}`,
                    borderRadius: '4px',
                    padding: '4px',
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: color,
                    fontWeight: 'bold'
                  }}
                  onClick={() => handleFieldClick(field)}
                  onMouseDown={(e) => {
                    // Handle dragging
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startFieldX = field.x;
                    const startFieldY = field.y;

                    const handleMouseMove = (e: MouseEvent) => {
                      if (!pdfContainerRef.current) return;
                      const rect = pdfContainerRef.current.getBoundingClientRect();
                      const deltaX = ((e.clientX - startX) / rect.width) * 100;
                      const deltaY = ((e.clientY - startY) / rect.height) * 100;

                      handleFieldUpdate(field.id, {
                        x: Math.max(0, Math.min(100 - field.width, startFieldX + deltaX)),
                        y: Math.max(0, Math.min(100 - field.height, startFieldY + deltaY))
                      });
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                >
                  <Icon className="me-1" />
                  <span style={{ 
                    fontWeight: field.mapping ? 'bold' : 'normal',
                    textDecoration: field.mapping ? 'none' : 'underline dotted'
                  }}>
                    {getFieldValue(field)}
                  </span>
                  {field.mapping && (
                    <Badge 
                      bg="success" 
                      style={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        fontSize: '10px'
                      }}
                    >
                      ✓
                    </Badge>
                  )}

                  {/* Resize handle */}
                  {selectedField?.id === field.id && (
                    <div
                      className="resize-handle"
                      style={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        width: 8,
                        height: 8,
                        backgroundColor: color,
                        cursor: 'se-resize'
                      }}
                      onMouseDown={(e) => handleResize(field, 'bottom-right', e)}
                    />
                  )}
                </div>
              );
            })}
              
              {/* Invisible overlay to handle drag and drop when dragging */}
              {isDragging && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '2px dashed #3b82f6',
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <div style={{ 
                  backgroundColor: 'white', 
                  padding: '20px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                  <p style={{ margin: 0, color: '#3b82f6', fontWeight: 'bold' }}>
                    Drop field here
                  </p>
                </div>
              </div>
            )}
            </PDFImageViewer>
            
            {/* Field Mapping Debugger */}
            <FieldMappingDebugger
              fields={fields}
              currentPage={currentPage}
              containerDimensions={containerDimensions}
              imageDimensions={pdfImages[currentPage - 1]}
            />
          </div>
        </div>

        {/* Properties Panel - Right Side */}
        {selectedField && (
          <Card className="properties-panel">
            <Card.Header>
              <h6 className="mb-0">Field Properties</h6>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedField.label}
                    onChange={(e) => handleFieldUpdate(selectedField.id, { label: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Field Mapping</Form.Label>
                  <Form.Select
                    value={selectedField.mapping || ''}
                    onChange={(e) => {
                      handleFieldUpdate(selectedField.id, { mapping: e.target.value });
                      // Auto-update field type based on placeholder
                      const placeholder = CLIENT_FIELD_PLACEHOLDERS.find(p => p.id === e.target.value);
                      if (placeholder && placeholder.fieldType !== selectedField.type) {
                        handleFieldUpdate(selectedField.id, { 
                          type: placeholder.fieldType as Field['type'],
                          label: placeholder.label
                        });
                      }
                    }}
                  >
                    <option value="">-- Select Client Field --</option>
                    
                    {/* High Priority Fields First */}
                    <optgroup label="🔥 High Priority - Essential Client Fields">
                      {getHighPriorityFields()
                        .filter(p => p.fieldType === selectedField.type)
                        .map(placeholder => (
                          <option key={placeholder.id} value={placeholder.id}>
                            {placeholder.label}
                          </option>
                        ))}
                    </optgroup>
                    
                    {/* Buyer Fields */}
                    <optgroup label="👥 Buyer Fields">
                      {getFieldsByParty('Buyer')
                        .filter(p => p.fieldType === selectedField.type && p.priority !== 'high')
                        .map(placeholder => (
                          <option key={placeholder.id} value={placeholder.id}>
                            {placeholder.label}
                          </option>
                        ))}
                    </optgroup>
                    
                    {/* Seller Fields */}
                    <optgroup label="🏠 Seller Fields">
                      {getFieldsByParty('Seller')
                        .filter(p => p.fieldType === selectedField.type && p.priority !== 'high')
                        .map(placeholder => (
                          <option key={placeholder.id} value={placeholder.id}>
                            {placeholder.label}
                          </option>
                        ))}
                    </optgroup>
                    
                    {/* Generic/Other Fields */}
                    <optgroup label="📝 Generic Fields">
                      {CLIENT_FIELD_PLACEHOLDERS
                        .filter(p => p.category === 'Generic' && p.fieldType === selectedField.type)
                        .map(placeholder => (
                          <option key={placeholder.id} value={placeholder.id}>
                            {placeholder.label}
                          </option>
                        ))}
                    </optgroup>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Maps to: {selectedField.mapping ? 
                      CLIENT_FIELD_PLACEHOLDERS.find(p => p.id === selectedField.mapping)?.label || 'Unknown' : 
                      'Not mapped'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Required Field"
                    checked={selectedField.required || false}
                    onChange={(e) => handleFieldUpdate(selectedField.id, { required: e.target.checked })}
                  />
                </Form.Group>

                <div className="text-muted small">
                  <FaInfoCircle className="me-1" />
                  Position: {Math.round(selectedField.x)}%, {Math.round(selectedField.y)}%
                  <br />
                  Size: {Math.round(selectedField.width)}% × {Math.round(selectedField.height)}%
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Save Confirmation */}
      {showSaveConfirm && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 9999 }}>
          <div className="alert alert-success alert-dismissible fade show">
            <strong>Field mapping saved!</strong> This mapping will be used for similar documents.
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentFieldMapper;
